import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ApplicationDetailTabs, CandidateHomeNavigationService } from '@candidate/app/services/candidate-home-navigation.service'
import { ICandidateJobVM, IEmployerVideoContent } from 'shared-lib'

type ICandidateJobVMKeys = keyof ICandidateJobVM

export interface IApplicationCopyInfo {
  originalJobTitle: string
  currentJobTitle: string
  originalJobPostId: string
  currentJobPostId: string
  originalApplicationDate?: Date
  copiedAt?: Date
}

@Component({
  selector: 'job-detail-display',
  template: `
    <!-- APPLICATION NOTICE FOR COPIED APPLICATION -->
    <div
      *ngIf="jobDetail?.reassignment && showApplicationNotice && !!jobDetail?.reassignment?.reassignedFrom"
      class="e11-w-full e11-p-4 e11-my-4 e11-rounded-md e11-flex e11-flex-col e11-items-start e11-gap-2 e11-relative"
      [ngClass]="{
        'e11-bg-skin-primary-accent/10': !isApplicationNoticeLoading,
        'e11-bg-skin-grey/10': isApplicationNoticeLoading
      }"
    >
      <h4 [ngClass]="{ 'e11-font-bold e11-mb-2': !isApplicationNoticeLoading, 'e11-skeleton e11-text-transparent': isApplicationNoticeLoading }">
        {{ 'Application Notice' | translate }}
      </h4>
      <span
        [ngClass]="{
          'e11-skeleton e11-text-transparent': isApplicationNoticeLoading
        }"
      >
        {{ 'Your application, originally submitted for the' | translate }}
        <ng-container *ngIf="applicationCopyInfo as info">
          <span class="e11-font-primary-demibold">
            <a
              *ngIf="info.originalJobPostId; else originalTitleOnly"
              href="#"
              class="e11-underline e11-cursor-pointer hover:e11-opacity-80"
              (click)="navigateToJob(info.originalJobPostId); $event.preventDefault()"
              >{{ info.originalJobTitle }}</a
            >
            <ng-template #originalTitleOnly>{{ info.originalJobTitle }}</ng-template>
          </span>
        </ng-container>
        {{ 'position, has been copied to the' | translate }}
        <ng-container *ngIf="applicationCopyInfo as info">
          <span class="e11-font-primary-demibold"> {{ info.currentJobTitle }} ({{ 'current application' | translate }}) </span>
        </ng-container>
        {{ 'position.' | translate }}
        {{
          'As confirmed during the submission of your original application, you consented to being considered for alternative roles within the organization.'
            | translate
        }}
        {{ "This copy was carried out at the employer's discretion to ensure the best fit within their open roles." | translate }}
      </span>
      <div
        [ngClass]="{
          'e11-text-xs e11-flex e11-flex-row e11-gap-4': !isApplicationNoticeLoading,
          'e11-skeleton e11-text-transparent': isApplicationNoticeLoading
        }"
      >
        <span *ngIf="applicationCopyInfo?.originalApplicationDate">
          {{ 'Original Application for' | translate }}
          <a
            href="#"
            class="e11-underline e11-cursor-pointer"
            (click)="applicationCopyInfo && navigateToJob(applicationCopyInfo.originalJobPostId); $event.preventDefault()"
            >{{ applicationCopyInfo?.originalJobTitle }}</a
          >
          {{ 'Submitted on' | translate }}:
          {{ applicationCopyInfo?.originalApplicationDate | date : 'MM/dd/yyyy' }}
        </span>
        <span *ngIf="applicationCopyInfo?.copiedAt">
          {{ 'Application Copied to' | translate }}
          <span class="e11-font-primary-demibold">{{ applicationCopyInfo?.currentJobTitle }}</span>
          {{ 'on' | translate }}:
          {{ applicationCopyInfo?.copiedAt | date : 'MM/dd/yyyy' }}
        </span>
      </div>

      <button
        type="button"
        [disabled]="isApplicationNoticeLoading"
        class="e11-absolute e11-right-0 e11-top-0 e11-m-2 e11-p-2 e11-flex e11-justify-center e11-items-center e11-rounded-full hover:e11-bg-skin-white e11-transition-all disabled:e11-cursor-not-allowed"
        (click)="onCloseApplicationNotice.emit()"
      >
        <span class="material-symbols-outlined"> close </span>
      </button>
    </div>

    <div class="e11-w-full e11-mb-8 md:e11-mb-4">
      <div class="e11-grid e11-grid-cols-1 md:e11-grid-cols-3 e11-gap-6">
        <e11-panel [containerClassOverrides]="'e11-h-full'">
          <div class="e11-grow">
            <div class="job-details ">
              <h5 class="e11-font-bold e11-mb-2">{{ 'Employment Information' | translate }}</h5>
              <div class="e11-flex e11-flex-wrap e11-gap-2 line-item e11-mb-0 e11-text-sm">
                <div *ngIf="jobDetail">{{ 'Employment Type' | translate }}:</div>
                <div *ngIf="!jobDetail" class="e11-skeleton-empty"></div>
                <div *ngIf="jobDetail && jobDetail.employmentType">
                  {{ jobDetail.employmentType | employmentType | translate }}
                </div>
                <div *ngIf="jobDetail && !jobDetail.employmentType">{{ 'Not Specified' | translate }}</div>
              </div>
              <div *ngIf="jobDetail" class="e11-flex e11-flex-wrap e11-gap-2 line-item e11-mb-0 e11-text-sm">
                <div>{{ 'Remote or On-Site' | translate }}:</div>
                <div>{{ jobDetail?.employeeLocation | employeeLocation | translate }}</div>
              </div>
              <div *ngIf="jobDetail" class="e11-flex e11-flex-wrap e11-gap-2 line-item e11-mb-0 e11-text-sm">
                <div>{{ 'Experience Level' | translate }}:</div>
                <div *ngIf="jobDetail.experienceLevel">
                  {{ jobDetail.experienceLevel | experienceLevel | translate }}
                </div>
                <div *ngIf="!jobDetail.experienceLevel">{{ 'Unspecified' | translate }}</div>
              </div>
            </div>
          </div>
        </e11-panel>
        <e11-panel [containerClassOverrides]="'e11-h-full'">
          <div class="e11-grow">
            <h5 class="e11-font-bold e11-mb-2">{{ 'Location' | translate }}</h5>
            <div *ngIf="!jobDetail || jobDetail.locationAddress">
              <p
                *ngIf="!jobDetail || jobDetail?.locationAddress?.address"
                class="e11-text-sm"
                [ngClass]="{ 'e11-mb-2': !jobDetail, 'e11-mb-0': jobDetail }"
              >
                {{ jobDetail?.locationAddress?.address }}
              </p>
              <p *ngIf="jobDetail && jobDetail.locationAddress?.address2" class="e11-text-sm e11-mb-0">{{ jobDetail.locationAddress?.address2 }}</p>
              <p *ngIf="jobDetail && jobDetail.locationAddress?.city" class="e11-text-sm e11-mb-0">
                {{ jobDetail.locationAddress?.city }} , {{ jobDetail.locationAddress?.state }} . {{ jobDetail.locationAddress?.zip }}
              </p>
              <p *ngIf="!jobDetail || jobDetail.locationAddress?.countryCode" class="e11-text-sm e11-mb-0">
                {{ jobDetail?.locationAddress?.countryCode }}
              </p>
            </div>

            <a [href]="getMapLink()" target="_blank" class="e11-block e11-no-underline" *ngIf="jobDetail?.locationAddress?.address">
              <div
                class="e11-text-sm e11-text-skin-primary-accent hover:e11-text-skin-primary e11-flex e11-flex-row e11-items-center e11-justify-start e11-cursor-pointer"
              >
                <span class="material-symbols-outlined e11-text-base e11-mr-1">assistant_direction</span>Map
              </div>
            </a>
          </div>
        </e11-panel>
        <e11-panel [containerClassOverrides]="'e11-h-full'">
          <div class="e11-grow e11-max-w-xs">
            <div class="e11-flex e11-gap-4">
              <!-- Job Post Video -->
              <div class="e11-flex-1">
                <div *ngIf="jobPostVideo; else noJobPostVideo">
                  <div
                    (click)="onClickVideo(jobPostVideo)"
                    class="e11-relative e11-cursor-pointer e11-rounded-t e11-overflow-hidden"
                    [ngStyle]="{
                      backgroundImage: 'url(' + jobPostVideo.thumb?.url + ')',
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      height: '85px'
                    }"
                  >
                    <div
                      class="e11-flex e11-w-full e11-h-full e11-items-center e11-justify-center e11-absolute e11-inset-0 e11-bg-black e11-bg-opacity-40 hover:e11-bg-opacity-50 e11-transition"
                    >
                      <span class="material-icons-outlined e11-text-white" style="font-size: 48px;"> play_circle </span>
                    </div>
                  </div>
                  <div class="e11-bg-skin-primary-accent e11-text-white e11-text-center e11-py-1.5 e11-font-semibold e11-text-xs e11-rounded-b">
                    Job Post Video
                  </div>
                </div>

                <ng-template #noJobPostVideo>
                  <div class="e11-rounded-t e11-flex e11-items-center e11-justify-center e11-bg-gray-100" style="height: 85px">
                    <p class="e11-text-gray-400 e11-text-xs e11-mb-0 e11-font-semibold">No Video<br />Available</p>
                  </div>
                  <div class="e11-bg-gray-400 e11-text-white e11-text-center e11-py-1.5 e11-font-semibold e11-text-xs e11-rounded-b">
                    Job Post Video
                  </div>
                </ng-template>
              </div>

              <!-- Company Video -->
              <div class="e11-flex-1">
                <div *ngIf="companyVideo; else noCompanyVideo">
                  <div
                    (click)="onClickVideo(companyVideo)"
                    class="e11-relative e11-cursor-pointer e11-rounded-t e11-overflow-hidden"
                    [ngStyle]="{
                      backgroundImage: 'url(' + companyVideo.thumb?.url + ')',
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      height: '85px'
                    }"
                  >
                    <div
                      class="e11-flex e11-w-full e11-h-full e11-items-center e11-justify-center e11-absolute e11-inset-0 e11-bg-black e11-bg-opacity-40 hover:e11-bg-opacity-50 e11-transition"
                    >
                      <span class="material-icons-outlined e11-text-white" style="font-size: 48px;"> play_circle </span>
                    </div>
                  </div>
                  <div class="e11-bg-skin-primary-accent e11-text-white e11-text-center e11-py-1.5 e11-font-semibold e11-text-xs e11-rounded-b">
                    Company Video
                  </div>
                </div>

                <ng-template #noCompanyVideo>
                  <div class="e11-rounded-t e11-flex e11-items-center e11-justify-center e11-bg-gray-100" style="height: 85px">
                    <p class="e11-text-gray-400 e11-text-xs e11-mb-0 e11-font-semibold">No Video<br />Available</p>
                  </div>
                  <div class="e11-bg-gray-400 e11-text-white e11-text-center e11-py-1.5 e11-font-semibold e11-text-xs e11-rounded-b">
                    Company Video
                  </div>
                </ng-template>
              </div>
            </div>
          </div>
        </e11-panel>
      </div>
    </div>
    <div class="e11-clear-both md:e11-clear-none"></div>
    <div class="e11-w-full">
      <e11-panel>
        <h5 class="e11-font-bold e11-mb-2">{{ 'Job Description' | translate }}</h5>
        <e11-divider [margin]="1"></e11-divider>
        <div *ngIf="jobDetail" [e11SummernoteView]="jobDetail.jobDescription || ''"></div>
        <ng-container *ngIf="!jobDetail">
          <p *ngFor="let i of 3 | range" class="e11-skeleton-empty"></p>
        </ng-container>
      </e11-panel>

      <e11-panel>
        <h5 class="e11-font-bold e11-mb-2">{{ 'Qualifications' | translate }}</h5>
        <e11-divider [margin]="1"></e11-divider>
        <div *ngIf="jobDetail" [e11SummernoteView]="jobDetail.minimumQualifications || ''"></div>
        <ng-container *ngIf="!jobDetail">
          <p *ngFor="let i of 3 | range" class="e11-skeleton-empty"></p>
        </ng-container>
      </e11-panel>

      <e11-panel>
        <div *ngIf="!jobDetail || jobDetail.responsibilities">
          <h5 class="e11-font-bold e11-mb-2">{{ 'Responsibilities' | translate }}</h5>
          <e11-divider [margin]="1"></e11-divider>
          <div *ngIf="jobDetail" [e11SummernoteView]="jobDetail.responsibilities || ''"></div>
          <ng-container *ngIf="!jobDetail">
            <p *ngFor="let i of 3 | range" class="e11-skeleton-empty"></p>
          </ng-container>
        </div>
      </e11-panel>

      <e11-panel>
        <div *ngIf="jobDetail" class="job-details">
          <div *ngFor="let key of modelKeys" class="row line-item e11-mb-3">
            <div class="col-xs-12 col-md-3 nopadding-left e11-font-bold">{{ translationModelKeys[key] | translate }}:</div>
            <div *ngIf="!jobDetail[key]" class="col-xs-12 col-md-8 nopadding-left">
              <span>{{ 'Not Specified' | translate }}</span>
            </div>
            <div *ngIf="jobDetail[key]" class="col-xs-12 col-md-8 nopadding-left">
              <span *ngIf="key === 'compensationLow' || key === 'compensationHigh'">
                {{ jobDetail[key] | currency }}
              </span>
              <span *ngIf="key === 'educationLevel'">
                {{ jobDetail[key] | degreeLevel | translate }}
              </span>
              <span *ngIf="key === 'department'">
                {{ jobDetail[key]?.name }}
              </span>
              <span *ngIf="key === 'benefits'">
                <div *ngIf="jobDetail" [e11SummernoteView]="jobDetail.benefits || ''"></div>
              </span>
              <span
                *ngIf="
                  key !== 'compensationLow' &&
                  key !== 'compensationHigh' &&
                  key !== 'scheduleText' &&
                  key !== 'educationLevel' &&
                  key !== 'benefits' &&
                  key !== 'department'
                "
              >
                {{ jobDetail[key] }}<span *ngIf="key === 'travelPercentage' && jobDetail[key]">%</span>
              </span>
              <ng-container *ngIf="key === 'scheduleText'">
                <job-schedule [schedule]="jobDetail.scheduleText || ''"></job-schedule>
              </ng-container>
            </div>
          </div>
        </div>
      </e11-panel>
    </div>
    <e11-panel>
      <div *ngIf="!jobDetail || jobDetail.legalDisclaimer" class="e11-w-full disclaimer">
        <h5 class="e11-font-bold e11-mb-2">{{ 'EEO Policy Statement' | translate }}</h5>
        <e11-divider [margin]="1"></e11-divider>
        <p *ngIf="!jobDetail" class="e11-skeleton-empty"></p>
        <div *ngIf="jobDetail && jobDetail.legalDisclaimer" [e11SummernoteView]="jobDetail!.legalDisclaimer || ''"></div>
      </div>
    </e11-panel>
  `,
})
export class JobDetailDisplayComponent {
  @Input() jobDetail?: ICandidateJobVM
  @Input() jobPostVideo?: IEmployerVideoContent | null
  @Input() companyVideo?: IEmployerVideoContent | null
  @Input() showApplicationNotice?: boolean
  @Input() applicationCopyInfo?: IApplicationCopyInfo
  @Input() isApplicationNoticeLoading?: boolean

  @Output() openVideoPlayer = new EventEmitter<IEmployerVideoContent>()
  @Output() onCloseApplicationNotice = new EventEmitter<void>()

  constructor(private navigationService: CandidateHomeNavigationService) {}

  navigateToJob(jobPostId: string) {
    this.navigationService.toApplicationDetailTab(jobPostId, ApplicationDetailTabs.JobDetail)
  }

  modelKeys: ICandidateJobVMKeys[] = [
    'educationLevel',
    'department',
    'scheduleText',
    'travelPercentage',
    'compensationLow',
    'compensationHigh',
    'benefits',
  ]

  translationModelKeys: any = {
    educationLevel: 'Education Level',
    department: 'Department',
    scheduleText: 'Work Schedule',
    travelPercentage: 'Travel Percentage',
    compensationLow: 'Compensation Low',
    compensationHigh: 'Compensation High',
    benefits: 'Benefits',
  }

  getMapLink(): string {
    if (!this.jobDetail || !this.jobDetail.locationAddress) {
      return '#'
    }

    const loc = this.jobDetail.locationAddress

    const address = [loc.address, loc.address2, loc.city, loc.state, loc.zip, loc.countryCode].filter(segment => segment).join(', ')

    const encodedAddress = encodeURIComponent(address)

    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
  }

  onClickVideo(video: IEmployerVideoContent): void {
    this.openVideoPlayer.emit(video)
  }
}
